'use client'

import * as React from 'react'

import type { DesktopHref } from './desktop-routing'
import { desktopHrefToString, resolveDesktopHref, resolveInstallerUrl } from './desktop-routing'

export type DesktopLinkProps = Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & {
  href: DesktopHref
}

function DefaultDesktopLink({ href, ...props }: DesktopLinkProps) {
  return <a href={desktopHrefToString(href)} {...props} />
}

const DesktopRoutingContext = React.createContext<{
  basePath: string
  installerOrigin: string
  Link: React.ComponentType<DesktopLinkProps>
}>({
  basePath: '',
  installerOrigin: '',
  Link: DefaultDesktopLink,
})

export function DesktopRoutingProvider({
  basePath,
  installerOrigin = '',
  Link,
  children,
}: {
  basePath: string
  installerOrigin?: string
  Link?: React.ComponentType<DesktopLinkProps>
  children: React.ReactNode
}) {
  const value = React.useMemo(
    () => ({
      basePath,
      installerOrigin,
      Link: Link ?? DefaultDesktopLink,
    }),
    [Link, basePath, installerOrigin],
  )

  return <DesktopRoutingContext value={value}>{children}</DesktopRoutingContext>
}

export function DesktopLink({ href, ...props }: DesktopLinkProps) {
  const { basePath, Link } = React.use(DesktopRoutingContext)
  if (!basePath) {
    return <Link href={href} {...props} />
  }

  return <Link href={resolveDesktopHref(basePath, href)} {...props} />
}

export function useInstallerUrl(href: string): string {
  const { installerOrigin } = React.use(DesktopRoutingContext)
  return resolveInstallerUrl(installerOrigin, href)
}
